import gsap from 'gsap'
import ScrollTrigger from 'gsap/ScrollTrigger'
import { GLOBAL_CONSTANTS } from 'assets/js/utils/constants.js'

const SELECTORS = {
  introVideoContainer: '.js-minecraft-hotspots__intro',
  video: '.js-card-video, .js-xray__video-video',
  introVideo: '.js-xray__video-video',
  dotsContainer: '.js-minecraft-hotspots__dots',
  hotspots: '.js-hotspot-button',
  blocks: '.js-minecraft-hotspots__block',
  backButton: '.js-back-button',
  blocksOut: '.js-minecraft-hotspots__block__zoom-out',
  xrayContainer: '.js-xray__video',
  skipButton: '.js-xray-skip-intro',
  marquee: '.js-marquee'
}

class MinecraftHotspots {
  constructor(element) {
    this.element = element
    this.backButton = this.element.querySelector(SELECTORS.backButton)

    /* Intro Video */
    this.introVideoContainer = this.element.querySelector(SELECTORS.introVideoContainer)
    this.introVideo = this.introVideoContainer.querySelector(SELECTORS.introVideo)
    this.skipButton = this.element.querySelector(SELECTORS.skipButton)
    this.marquee = this.element.querySelector(SELECTORS.marquee)

    /* Hotspots */
    this.dotsContainer = this.element.querySelector(SELECTORS.dotsContainer)
    this.hotspots = this.dotsContainer.querySelectorAll(SELECTORS.hotspots)
    this.emitter = GLOBAL_CONSTANTS.UTILS.EMITTER

    /* Blocks */
    this.blocks = this.element.querySelectorAll(SELECTORS.blocks)
    this.blocksOut = this.element.querySelectorAll(SELECTORS.blocksOut)

    this.init()
  }

  init() {
    /* Register event listeners: */
    this.registerEvents()

    /* Handle video states: */
    this.blocks.forEach(block => {
      const video = block.querySelector(SELECTORS.video)

      video.classList.remove('js-play-in-view')
      video.pause()
      video.currentTime = 0

      video.addEventListener('ended', () => this.backButton.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE))
    })
    this.blocksOut.forEach(block => {
      const video = block.querySelector(SELECTORS.video)

      video.classList.remove('js-play-in-view')
      video.pause()
      video.currentTime = 0
    })

    gsap.timeline({
      scrollTrigger: {
        trigger: this.element,
        start: '-25% top',
        end: 'bottom',
        scrub: 1,
        once: true,
        onEnter: () => this.introVideo.play()
      }
    })

    ScrollTrigger.refresh()

    if (document.documentElement.classList.contains('reduced-motion')) {
      this.introVideo.onplay()
      this.introVideo.onended()
    }
  }

  registerEvents() {
    this.skipButton.addEventListener('click', () => {
      this.introVideo.currentTime = this.introVideo.duration
    })

    if (this.introVideo.readyState >= 2) {
      this.element.classList.remove('-preload')
    } else {
      this.introVideo.addEventListener('canplay', () => {
        this.element.classList.remove('-preload')
      })
    }

    this.introVideo.onplay = () => {
      this.skipButton.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
      this.introVideoStarted()
    }

    this.introVideo.onended = () => {
      this.hideIntroVideo()
    }

    this.hotspots.forEach(hotspot => {
      hotspot.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.handleHotspotClick.bind(this))
    })

    this.backButton.addEventListener(GLOBAL_CONSTANTS.EVENTS.CLICK, this.handleBackButtonClick.bind(this))

    // Emitter for hiding pan instructions.
    this.emitter.on(GLOBAL_CONSTANTS.EVENTS.XRAY_IS_ACTIVE, ({ element, isActive }) => {
      if (this.element.contains(element)) {
        if (isActive) {
          this.backButton.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
        } else {
          this.backButton.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
        }
      }
    })
  }

  introVideoStarted() {
    this.element.classList.add('-intro')
  }

  hideIntroVideo() {
    this.introVideoContainer.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
    this.dotsContainer.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    this.marquee.classList.remove('-animate')
  }

  handleHotspotClick(event) {
    const hotspot = event.target
    const blockNumber = hotspot.dataset.block
    const block = this.element.querySelector(`${SELECTORS.blocks}[data-block="${blockNumber}"]`)
    const video = block.querySelector(SELECTORS.video)

    this.dotsContainer.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)

    block.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    block.querySelector(SELECTORS.xrayContainer).classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
    this.backButton.dataset.block = blockNumber

    if (!document.documentElement.classList.contains('reduced-motion')) {
      video.play()
    } else {
      video.onended()
    }
  }

  handleBackButtonClick() {
    const blockNumber = this.backButton.dataset.block

    this.backButton.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)

    this.blocks.forEach(block => {
      const video = block.querySelector(SELECTORS.video)
      block.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)

      video.pause()
      video.currentTime = 0
    })

    this.blocksOut.forEach(block => {
      if (block.dataset.block === blockNumber) {
        const video = block.querySelector(SELECTORS.video)

        video.onended = () => {
          block.classList.add(GLOBAL_CONSTANTS.CLASSES.HIDE)
          this.dotsContainer.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)
        }

        block.classList.remove(GLOBAL_CONSTANTS.CLASSES.HIDE)

        if (!document.documentElement.classList.contains('reduced-motion')) {
          video.play()
        } else {
          video.onended()
        }
      }
    })
  }
}

export default {
  selector: '.js-minecraft-hotspots',
  Source: MinecraftHotspots
}
